// components/Modals/UpdateServiceModal/UpdateServiceModal.tsx
import React, { useContext, useEffect, useState } from 'react'
import { Modal, Button } from 'react-bootstrap'
import { AppContext } from 'store/context'
import { ActionTypes } from 'store/actions'
import {
  SERVICES_LIST,
  SERVICE_AUTRE_VALUE,
} from 'utils/__consts/Common/Service'
import {
  dispatchUserInformations,
  updateUserFields,
} from 'services/user-services'
import { useTranslation } from 'react-i18next'
import CustomSelect from 'components/Selectors/CustomSelect'
import { loginWithRefreshToken } from 'services/connexion-services'
import COLORS from 'utils/__enums/Common/Color'
import Loading from 'components/Loaders/Loading'

interface ServiceOption {
  label: string
  value: string
}

// La modale est obligatoire : elle ne peut être fermée par l'utilisateur
// (ni via la croix, ni via l'overlay, ni via Echap). Ce handler ne fait
// donc volontairement rien.
const preventClose = (): void => undefined

const UpdateServiceModal: React.FC = () => {
  const { state, dispatch } = useContext(AppContext)

  const { t } = useTranslation()

  const isOpen = state.user.service === SERVICE_AUTRE_VALUE

  const serviceOptions: ServiceOption[] = SERVICES_LIST.map((service) => ({
    label: service,
    value: service,
  }))

  // Si la valeur existante est "Autre", le champ doit s'afficher vide
  const [selectedService, setSelectedService] = useState<ServiceOption | null>(
    null
  )
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasError, setHasError] = useState(false)

  const handleValidate = async () => {
    if (!selectedService) return
    setIsSubmitting(true)
    setHasError(false)

    const success = await updateUserFields(state.user.userId, {
      service: selectedService.value,
    })

    const refreshToken = localStorage.getItem('refresh_token')
    loginWithRefreshToken(refreshToken || '')
    const userData = await loginWithRefreshToken(refreshToken || '')
    dispatchUserInformations(
      { ...userData, backgroundColor: COLORS.GRAY_0 },
      dispatch,
      state
    )

    setIsSubmitting(false)

    if (success) {
      dispatch({
        type: ActionTypes.SET_USER_SERVICE,
        payload: { service: selectedService.value },
      })
    } else {
      setHasError(true)
    }
  }

  return (
    <Modal
      show={isOpen}
      onHide={preventClose}
      backdrop="static"
      keyboard={false}
      centered
    >
      <Modal.Header>
        <Modal.Title id="update-service-modal-title">
          {t('updateServiceModal.title')}
        </Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <p>{t('updateServiceModal.description')}</p>

        <div>{t('updateServiceModal.label')}</div>
        <CustomSelect
          id="update-service-select"
          name="update-service-select"
          options={serviceOptions}
          value={selectedService}
          onChange={(option: ServiceOption) => setSelectedService(option)}
          placeholder={t('updateServiceModal.placeholder')}
          isSearchable
        />

        {hasError && (
          <p className="update-service-modal__error">
            {t('updateServiceModal.error')}
          </p>
        )}
      </Modal.Body>

      <Modal.Footer>
        <div className="sessionExpiration__actions">
          <button
            className="generic-button form__submit"
            type="submit"
            disabled={!selectedService || isSubmitting}
            onClick={handleValidate}
          >
            <Loading isLoading={isSubmitting} inButton>
              {t('updateServiceModal.actions.validate')}
            </Loading>
          </button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}

export default UpdateServiceModal
